"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface PopoverProps {
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "start" | "end" | "center";
  side?: "bottom" | "top";
  className?: string;
  width?: number;
}

type Pos = { top: number; left: number; placement: "bottom" | "top" };

// Portals popover content to document.body so it never gets clipped by
// `overflow-hidden` ancestors (e.g. when the trigger lives inside a Modal).
// Position is computed from the trigger's bounding rect, then re-measured on
// scroll / resize. Falls back to opening upward if there's not enough room
// below the trigger.
export function Popover({
  trigger,
  children,
  align = "start",
  side = "bottom",
  className,
  width = 240,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMounted(true), []);

  const compute = useCallback((): Pos | null => {
    const t = triggerRef.current;
    if (!t) return null;
    const rect = t.getBoundingClientRect();
    const gap = 4;
    const popH = popRef.current?.offsetHeight ?? 240;
    const popW = popRef.current?.offsetWidth ?? width;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    let placement: "bottom" | "top" = side;
    if (side === "bottom" && rect.bottom + popH + gap > vh && rect.top - popH - gap > 0) {
      placement = "top";
    } else if (side === "top" && rect.top - popH - gap < 0 && rect.bottom + popH + gap < vh) {
      placement = "bottom";
    }

    const top = placement === "bottom" ? rect.bottom + gap : rect.top - popH - gap;
    let left: number;
    if (align === "end") left = rect.right - popW;
    else if (align === "center") left = rect.left + rect.width / 2 - popW / 2;
    else left = rect.left;
    left = Math.max(8, Math.min(left, vw - popW - 8));

    return { top, left, placement };
  }, [align, side, width]);

  useLayoutEffect(() => {
    if (!open) return;
    setPos(compute());
    const recompute = () => setPos(compute());
    const r = requestAnimationFrame(recompute);
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(r);
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open, compute]);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {trigger}
      </div>
      {mounted &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                ref={popRef}
                initial={{ opacity: 0, y: pos?.placement === "top" ? 6 : -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.12 }}
                style={{
                  position: "fixed",
                  top: pos?.top ?? -9999,
                  left: pos?.left ?? -9999,
                  width: `min(${width}px, calc(100vw - 16px))`,
                  visibility: pos ? "visible" : "hidden",
                }}
                className={cn(
                  "z-[70] rounded-lg border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-lg glass",
                  className
                )}
              >
                {children(() => setOpen(false))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
